/**
 * Underwriting Pulse — cyber-insurer threat-advisory feed.
 *
 * Six insurtech carriers (Coalition / At-Bay / Resilience / Cowbell /
 * Corvus / Beazley) publish public threat advisories that track real
 * claim activity. By the time their actuarial team has digested the
 * loss data and posted, the cost basis is settled losses — not
 * theoretical CVSS exposure.
 *
 * Filters:
 *   - Theme chips (top of page) — clickable, OR-ed against the table
 *   - Carrier chips                — clickable, narrow to one carrier
 *   - Days slider                  — 7 / 14 / 30 day window
 *
 * Empty state messaging assumes the cron may still be in flight on a
 * fresh deployment.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ShieldAlert, RefreshCw, ExternalLink,
  Filter, X, Building2, Thermometer, ArrowUp, ArrowDown,
  Activity, BarChart3, Landmark, Newspaper, FileText,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import SharedSparkline from '../components/Sparkline'
import MiniBarChart from '../components/MiniBarChart'
import PageHeader from '../components/PageHeader'
import ErrorBanner from '../components/ErrorBanner'

interface Advisory {
  carrier:      string
  carrier_name: string
  title:        string
  summary:      string
  url:          string
  published_at: string | null
  themes:       string[]
  cves:         string[]
  sectors:      string[]
  controls:     string[]
  id:           string
  carrier_kind?: 'primary' | 'reinsurer'
}

interface IndexComponent {
  name:       string
  value:      number | null
  change_pct: number | null
}

interface CoalitionIndex {
  value:      number
  period:     string
  mom_pct:    number | null
  components: IndexComponent[]
  source_url: string
  fetched_at: string
}

interface FeedResp {
  advisories: Advisory[]
  count:      number
  by_carrier: Record<string, number>
  carriers:   Record<string, string>
  carrier_kinds?: Record<string, 'primary' | 'reinsurer'>
  fetched_at: string | null
  errors:     Array<{ carrier: string; error: string }>
  index?:     { coalition?: CoalitionIndex }
}

interface NaicRow {
  id: string
  year: number
  carrier_name: string
  naic_group_code: string
  gross_written_premium: number
  direct_loss_ratio: number | null
  defense_cost_ratio: number | null
  num_policies: number | null
  source_url: string
  fetched_at: string | null
}

interface EiopaRow {
  id: string
  quarter: string
  country_iso2: string
  line_of_business: string
  gross_written_premium_eur: number
  gross_claims_paid_eur: number | null
  source_url: string
  fetched_at: string | null
}

interface SecRow {
  id: string
  filing_id: string
  ticker: string
  carrier_name: string
  period_end_date: string
  form_type: string
  cyber_premium_usd: number | null
  cyber_loss_ratio: number | null
  narrative_excerpt: string
  source_url: string
  extracted_at: string | null
}

interface PrItem {
  id: string
  feed: string
  title: string
  summary: string
  url: string
  published_at: string | null
  carrier: string
  direction: string
  magnitude_pct: number | null
  period: string
  fetched_at: string | null
}

interface Pulse {
  id: string
  quarter: string
  broker: string
  line: string
  change_pct: number
  window: 'yoy' | 'qoq'
  source_url: string
  notes: string
  recorded_by: string
  recorded_at: string | null
  created_at: string | null
}

interface Benchmark {
  id: string
  year: number
  source: string
  metric: string
  value: number
  unit: 'USD' | 'PCT' | 'DAYS'
  sector: string
  notes: string
  source_url: string
  recorded_by: string
  recorded_at: string | null
  created_at: string | null
}

interface ThemesResp {
  themes:     Record<string, number>
  total:      number
  fetched_at: string | null
}

interface CoalitionSpark {
  points: number[]
  labels: string[]
  note:   string
}
interface RegulatorySpark {
  naic:  { points: number[]; labels: string[] }
  eiopa: { points: number[]; labels: string[] }
  sec:   { points: number[]; labels: string[] }
}
interface AdvisoriesDailySpark {
  days:       string[]
  by_carrier: Record<string, number[]>
}
interface ReinsuranceWeeklySpark {
  weeks:      string[]
  by_carrier: Record<string, number[]>
}
interface PrWeeklySpark {
  weeks:  string[]
  points: number[]
}

const DAY_OPTIONS = [7, 14, 30] as const

function formatRelative(iso: string | null): string {
  if (!iso) return 'undated'
  try {
    const dt = new Date(iso)
    const diffMs = Date.now() - dt.getTime()
    const days = Math.floor(diffMs / (24 * 3600 * 1000))
    if (days < 0) return dt.toISOString().slice(0, 10)
    if (days === 0) return 'today'
    if (days === 1) return 'yesterday'
    if (days < 7)  return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    return dt.toISOString().slice(0, 10)
  } catch {
    return iso.slice(0, 10)
  }
}

const CARRIER_BADGE: Record<string, string> = {
  coalition:    'bg-blue/10 text-blue border-blue/30',
  at_bay:       'bg-violet/10 text-violet border-violet/30',
  resilience:   'bg-emerald/10 text-emerald border-emerald/30',
  cowbell:      'bg-amber/10 text-amber-light border-amber/30',
  corvus:       'bg-rose/10 text-rose-light border-rose/30',
  beazley:      'bg-cyan/10 text-cyan border-cyan/30',
  // Reinsurance brokers — neutral grey palette so the primary-carrier
  // colours stay distinct in the table.
  guy_carpenter: 'bg-text-muted/10 text-text-primary border-text-muted/40',
  aon_re:        'bg-text-muted/10 text-text-primary border-text-muted/40',
  howden_re:     'bg-text-muted/10 text-text-primary border-text-muted/40',
}

export default function UnderwritingPage() {
  const [feed, setFeed]       = useState<FeedResp | null>(null)
  const [themes, setThemes]   = useState<ThemesResp | null>(null)
  const [pulses, setPulses]   = useState<Pulse[]>([])
  const [benches, setBenches] = useState<Benchmark[]>([])
  const [naic, setNaic]       = useState<NaicRow[]>([])
  const [eiopa, setEiopa]     = useState<EiopaRow[]>([])
  const [sec, setSec]         = useState<SecRow[]>([])
  const [prItems, setPrItems] = useState<PrItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [days, setDays]       = useState<number>(14)
  const [activeTheme, setActiveTheme]     = useState<string | null>(null)
  const [activeCarrier, setActiveCarrier] = useState<string | null>(null)
  const [carrierKind, setCarrierKind]     = useState<'all' | 'primary' | 'reinsurer'>('all')
  // Sparkline payloads — best-effort, fail-soft (each call defaults to {} on error).
  const [coalSpark, setCoalSpark]    = useState<CoalitionSpark | null>(null)
  const [regSpark, setRegSpark]      = useState<RegulatorySpark | null>(null)
  const [advSpark, setAdvSpark]      = useState<AdvisoriesDailySpark | null>(null)
  const [reSpark, setReSpark]        = useState<ReinsuranceWeeklySpark | null>(null)
  const [prSpark, setPrSpark]        = useState<PrWeeklySpark | null>(null)

  const load = async (refresh = false) => {
    setLoading(true)
    setError('')
    try {
      const [f, t, p, b, n, e, s, pr, csp, rsp, asp, resp_, psp] = await Promise.all([
        api.get('/api/underwriting/feed', { params: { days, refresh } }),
        api.get('/api/underwriting/themes'),
        api.get('/api/underwriting/pricing/pulse', { params: { limit: 500 } })
          .catch(() => ({ data: { items: [] } })),
        api.get('/api/underwriting/pricing/benchmarks', { params: { limit: 500 } })
          .catch(() => ({ data: { items: [] } })),
        api.get('/api/underwriting/regulatory/naic', { params: { limit: 200 } })
          .catch(() => ({ data: { items: [] } })),
        api.get('/api/underwriting/regulatory/eiopa', { params: { limit: 200 } })
          .catch(() => ({ data: { items: [] } })),
        api.get('/api/underwriting/regulatory/sec', { params: { limit: 50 } })
          .catch(() => ({ data: { items: [] } })),
        api.get('/api/underwriting/pr/items', { params: { days: 60, limit: 30 } })
          .catch(() => ({ data: { items: [] } })),
        api.get('/api/underwriting/sparklines/coalition-index', { params: { days: 30 } })
          .catch(() => ({ data: { points: [], labels: [], note: 'unavailable' } })),
        api.get('/api/underwriting/sparklines/regulatory')
          .catch(() => ({ data: { naic: { points: [], labels: [] },
                                   eiopa: { points: [], labels: [] },
                                   sec: { points: [], labels: [] } } })),
        api.get('/api/underwriting/sparklines/advisories-daily', { params: { days: 30 } })
          .catch(() => ({ data: { days: [], by_carrier: {} } })),
        api.get('/api/underwriting/sparklines/reinsurance-weekly', { params: { weeks: 8 } })
          .catch(() => ({ data: { weeks: [], by_carrier: {} } })),
        api.get('/api/underwriting/sparklines/pr-weekly', { params: { weeks: 8 } })
          .catch(() => ({ data: { weeks: [], points: [] } })),
      ])
      setFeed(f.data as FeedResp)
      setThemes(t.data as ThemesResp)
      setPulses((p.data?.items || []) as Pulse[])
      setBenches((b.data?.items || []) as Benchmark[])
      setNaic((n.data?.items || []) as NaicRow[])
      setEiopa((e.data?.items || []) as EiopaRow[])
      setSec((s.data?.items || []) as SecRow[])
      setPrItems((pr.data?.items || []) as PrItem[])
      setCoalSpark(csp.data as CoalitionSpark)
      setRegSpark(rsp.data as RegulatorySpark)
      setAdvSpark(asp.data as AdvisoriesDailySpark)
      setReSpark(resp_.data as ReinsuranceWeeklySpark)
      setPrSpark(psp.data as PrWeeklySpark)
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [days])

  const filtered = useMemo(() => {
    if (!feed) return [] as Advisory[]
    return feed.advisories.filter(a => {
      if (activeTheme && !(a.themes || []).includes(activeTheme))     return false
      if (activeCarrier && a.carrier !== activeCarrier)                 return false
      if (carrierKind === 'primary'   && a.carrier_kind === 'reinsurer') return false
      if (carrierKind === 'reinsurer' && a.carrier_kind !== 'reinsurer') return false
      return true
    })
  }, [feed, activeTheme, activeCarrier, carrierKind])

  const topThemes = useMemo(() => {
    if (!themes) return [] as Array<[string, number]>
    return Object.entries(themes.themes).slice(0, 18)
  }, [themes])

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        icon={ShieldAlert} iconClassName="text-cyan" title="UNDERWRITING PULSE"
        subtitle="cyber-insurer threat advisories · loss-experience-priced · refreshed hourly"
        backTo="/app" backLabel="Dashboard"
        actions={<>
          <div className="inline-flex rounded border border-border overflow-hidden">
            {DAY_OPTIONS.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={clsx(
                  'px-2 py-1 text-[11px] transition-colors',
                  d === days
                    ? 'bg-surface-raised text-text-primary'
                    : 'bg-surface-card text-text-muted hover:bg-surface-raised',
                )}
              >
                {d}d
              </button>
            ))}
          </div>
          <button type="button" onClick={() => void load(true)} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </>}
      />

      <ErrorBanner message={error} />

      {/* Coalition Cyber Threat Index hero strip */}
      <CoalitionIndexStrip index={feed?.index?.coalition} spark={coalSpark} />

      {/* Premium movement + loss distribution */}
      <div className="grid md:grid-cols-2 gap-4">
        <PremiumMovementPanel pulses={pulses} />
        <LossDistributionPanel benches={benches} />
      </div>

      {/* Regulatory premium-data panel — NAIC + EIOPA + SEC EDGAR */}
      <RegulatoryPanel naic={naic} eiopa={eiopa} sec={sec} spark={regSpark} />

      {/* Carrier-kind chip filter (primary / reinsurer) */}
      <div className="card p-2 flex items-center gap-2 flex-wrap text-[11px]">
        <Filter size={12} className="text-text-muted ml-1" />
        <span className="text-text-muted uppercase tracking-wider text-[10px]">Kind:</span>
        {(['all', 'primary', 'reinsurer'] as const).map(k => (
          <button
            key={k}
            type="button"
            onClick={() => setCarrierKind(k)}
            className={clsx(
              'px-2 py-0.5 rounded border transition-colors capitalize',
              carrierKind === k
                ? 'bg-cyan/15 border-cyan/50 text-cyan'
                : 'bg-surface-card border-border text-text-muted hover:bg-surface-raised',
            )}
          >
            {k}
          </button>
        ))}
      </div>

      {/* Carrier chips */}
      {feed && (
        <div className="card p-2 flex items-center gap-2 flex-wrap text-[11px]">
          <Building2 size={12} className="text-text-muted ml-1" />
          <span className="text-text-muted uppercase tracking-wider text-[10px]">Carriers:</span>
          {Object.entries(feed.carriers).map(([cid, name]) => {
            const count = feed.by_carrier[cid] ?? 0
            const active = activeCarrier === cid
            return (
              <button
                key={cid}
                type="button"
                onClick={() => setActiveCarrier(active ? null : cid)}
                className={clsx(
                  'px-2 py-0.5 rounded border transition-colors',
                  active ? CARRIER_BADGE[cid] || 'bg-surface-raised border-cyan/40' :
                           'bg-surface-card border-border text-text-muted hover:bg-surface-raised',
                )}
              >
                {name} <span className="opacity-60">({count})</span>
              </button>
            )
          })}
          {activeCarrier && (
            <button
              type="button"
              onClick={() => setActiveCarrier(null)}
              className="btn-ghost text-[10px] ml-auto"
              aria-label="Clear carrier filter"
            >
              <X size={10} /> clear
            </button>
          )}
        </div>
      )}

      {/* Theme chips */}
      {topThemes.length > 0 && (
        <div className="card p-2 flex items-center gap-2 flex-wrap text-[11px]">
          <Filter size={12} className="text-text-muted ml-1" />
          <span className="text-text-muted uppercase tracking-wider text-[10px]">Themes:</span>
          {topThemes.map(([theme, count]) => {
            const active = activeTheme === theme
            return (
              <button
                key={theme}
                type="button"
                onClick={() => setActiveTheme(active ? null : theme)}
                className={clsx(
                  'px-2 py-0.5 rounded border transition-colors font-mono text-[10px]',
                  active
                    ? 'bg-cyan/15 border-cyan/50 text-cyan'
                    : 'bg-surface-card border-border text-text-muted hover:bg-surface-raised',
                )}
              >
                {theme.replace('ransomware_group:', '#')} <span className="opacity-60">({count})</span>
              </button>
            )
          })}
          {activeTheme && (
            <button
              type="button"
              onClick={() => setActiveTheme(null)}
              className="btn-ghost text-[10px] ml-auto"
              aria-label="Clear theme filter"
            >
              <X size={10} /> clear
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && feed && filtered.length === 0 && (
        <div className="card p-4 text-text-muted text-sm">
          {feed.count === 0 ? (
            <>
              <div className="text-text-primary font-medium mb-1">No underwriting advisories yet</div>
              <div className="text-[12px]">
                The connector polls hourly across six insurtech carriers (Coalition,
                At-Bay, Resilience, Cowbell, Corvus, Beazley). The first pull may
                still be in flight, or all six carriers happened to be unreachable
                at the last refresh. Try the refresh button above.
              </div>
            </>
          ) : (
            <div className="text-[12px]">
              No advisories match the current filters. Try clearing the theme /
              carrier or widening the day window.
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-text-muted border-b border-border bg-surface-card flex items-center gap-3">
            <span>{filtered.length} advisory{filtered.length === 1 ? '' : 'ies'}</span>
            <AdvisoriesDailyMini spark={advSpark} />
            {feed?.fetched_at && (
              <span className="ml-auto text-text-muted">
                last pull: {formatRelative(feed.fetched_at)}
              </span>
            )}
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-text-muted border-b border-border bg-surface-card">
                <th className="px-3 py-2 w-24">Carrier</th>
                <th className="px-3 py-2 w-20">When</th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2 w-56">Themes</th>
                <th className="px-3 py-2 w-44">CVEs</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(adv => (
                <tr key={adv.id} className="border-b border-border hover:bg-surface-raised">
                  <td className="px-3 py-2 align-top">
                    <span className={clsx(
                      'inline-block px-2 py-0.5 rounded border font-mono text-[10px]',
                      CARRIER_BADGE[adv.carrier] || 'bg-surface-card border-border text-text-muted',
                    )}>
                      {adv.carrier_name}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top text-text-muted whitespace-nowrap">
                    {formatRelative(adv.published_at)}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {adv.url ? (
                      <a
                        href={adv.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-text-primary hover:text-cyan inline-flex items-start gap-1"
                      >
                        <span className="leading-tight">{adv.title}</span>
                        <ExternalLink size={10} className="flex-shrink-0 mt-0.5 opacity-60" />
                      </a>
                    ) : (
                      <span className="text-text-primary">{adv.title}</span>
                    )}
                    {adv.summary && (
                      <div className="text-[10px] text-text-muted mt-1 line-clamp-2">
                        {adv.summary}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {(adv.themes || []).slice(0, 4).map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setActiveTheme(t)}
                          className="px-1.5 py-0 rounded border border-border bg-surface-card text-[9px] font-mono text-text-muted hover:text-cyan hover:border-cyan/40"
                        >
                          {t.replace('ransomware_group:', '#')}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {(adv.cves || []).slice(0, 4).map(c => (
                        <Link
                          key={c}
                          to={`/app/cve/${c}`}
                          className="px-1.5 py-0 rounded border border-amber/30 bg-amber/5 text-[9px] font-mono text-amber-light hover:bg-amber/15"
                        >
                          {c}
                        </Link>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reinsurance pulse — small bar chart of advisories per reinsurer per week */}
      <ReinsurancePulsePanel spark={reSpark} />

      {/* PR / Business Wire premium-movement panel — shown below the
          advisory table because it's a lower-signal feed (best-effort
          LLM tagging) than the carrier advisories. */}
      <PrItemsPanel items={prItems} spark={prSpark} />
    </div>
  )
}


// ── Coalition Cyber Threat Index hero strip ─────────────────────────────────

function CoalitionIndexStrip(
  { index, spark }: { index: CoalitionIndex | undefined; spark: CoalitionSpark | null },
) {
  if (!index) {
    return (
      <div className="card p-2 text-[11px] text-text-muted flex items-center gap-2">
        <Thermometer size={12} className="text-amber-light" />
        <span>Coalition Cyber Threat Index — feed temporarily empty (will retry within 4h).</span>
      </div>
    )
  }
  const mom = index.mom_pct
  const arrow = mom == null
    ? null
    : mom >= 0
      ? <ArrowUp size={11} className="text-rose-light inline" />
      : <ArrowDown size={11} className="text-emerald-light inline" />

  const sparkPoints = spark?.points || []
  const showSpark = sparkPoints.length > 0

  return (
    <a
      href={index.source_url || 'https://www.coalitioninc.com/cyber-threat-index'}
      target="_blank"
      rel="noreferrer noopener"
      className="card p-2 text-[11px] flex items-center gap-3 flex-wrap hover:bg-surface-raised transition-colors"
    >
      <Thermometer size={14} className="text-amber-light" />
      <span className="text-text-primary font-semibold">
        Coalition Cyber Threat Index: <span className="font-mono">{index.value.toFixed(0)}</span>
      </span>
      {index.period && (
        <span className="text-text-muted">· {index.period}</span>
      )}
      {mom != null && (
        <span className={clsx('inline-flex items-center gap-1',
                              mom >= 0 ? 'text-rose-light' : 'text-emerald-light')}>
          {arrow} {Math.abs(mom).toFixed(1)}% MoM
        </span>
      )}
      {/* 30-day sparkline of the index value (single dot until the daily
          buffer accumulates more entries). */}
      {showSpark && (
        <SharedSparkline
          points={sparkPoints.map((v, i) => ({ t: spark?.labels?.[i] || '', v }))}
          width={120} height={20} showDot={true} showDelta={false}
          tone="amber" className="opacity-90"
        />
      )}
      {!showSpark && (
        <span className="text-text-muted text-[10px] italic">{spark?.note || 'data accumulating'}</span>
      )}
      {(index.components || []).slice(0, 4).map(c => (
        <span key={c.name} className="text-text-muted">
          · {c.name.replace(/_/g, ' ')}
          {c.value != null && <> <span className="font-mono text-text-primary">{c.value.toFixed(0)}</span></>}
        </span>
      ))}
      <span className="ml-auto text-text-muted inline-flex items-center gap-1">
        coalitioninc.com <ExternalLink size={9} />
      </span>
    </a>
  )
}


// ── Premium movement panel ──────────────────────────────────────────────────

function PremiumMovementPanel({ pulses }: { pulses: Pulse[] }) {
  // Group rows by broker so the "latest per broker" lookup is one map walk.
  const byBroker = useMemo(() => {
    const m: Record<string, Pulse[]> = {}
    for (const p of pulses) {
      if (!m[p.broker]) m[p.broker] = []
      m[p.broker].push(p)
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => a.quarter.localeCompare(b.quarter))
    }
    return m
  }, [pulses])

  const brokerKeys = Object.keys(byBroker).sort()

  return (
    <Link
      to="/app/admin/underwriting-pricing"
      className="card p-3 hover:bg-surface-raised transition-colors block space-y-2"
    >
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <Activity size={13} className="text-cyan" />
        <h3 className="text-[12px] font-semibold text-text-primary">Premium Movement</h3>
        <span className="text-[10px] text-text-muted">latest broker pulse · sparkline last 8 quarters</span>
      </div>

      {brokerKeys.length === 0 ? (
        <div className="text-[11px] text-text-muted py-2">
          No data — admins can populate this at <span className="text-cyan">/app/admin/underwriting-pricing</span>.
        </div>
      ) : (
        <div className="space-y-1.5">
          {brokerKeys.map(broker => {
            const rows = byBroker[broker]
            const latest = rows[rows.length - 1]
            // Wider window — last 8 quarters minimum density target.
            const series = rows.slice(-8)
            const isUp = latest.change_pct > 0
            return (
              <div key={broker} className="flex items-center gap-2 text-[11px]">
                <span className="capitalize w-20 truncate">{broker}</span>
                <span className={clsx('font-mono font-semibold w-16',
                                      isUp ? 'text-rose-light' : 'text-emerald-light')}>
                  {isUp ? '↑' : '↓'} {Math.abs(latest.change_pct).toFixed(1)}%
                </span>
                <span className="text-text-muted text-[10px] w-14">{latest.window.toUpperCase()} · {latest.quarter}</span>
                <SharedSparkline
                  points={series.map(r => ({ t: r.quarter, v: r.change_pct }))}
                  width={120} height={20} tone="down-good" showDot={true}
                  showDelta={false} className="flex-1"
                />
              </div>
            )
          })}
        </div>
      )}
    </Link>
  )
}


// ── Inline-SVG sparkline ────────────────────────────────────────────────────
//
// We standardised on the shared `<Sparkline />` component imported as
// `SharedSparkline` at the top of this file. The previous local
// implementation has been removed; per-panel SVG duplication lives in
// the shared `frontend/src/components/Sparkline.tsx`.


// ── Loss distribution panel ─────────────────────────────────────────────────

interface KpiTile {
  label: string
  metric: string
  fallback?: string
  preferredSources: string[]
}

const LOSS_TILES: KpiTile[] = [
  { label: 'Median ransom paid', metric: 'median_ransom',     preferredSources: ['netdiligence', 'allianz'] },
  { label: 'Mean BI loss',       metric: 'mean_bi_loss',      preferredSources: ['allianz', 'netdiligence'] },
  { label: 'Median total claim', metric: 'median_total_claim', preferredSources: ['netdiligence', 'allianz'] },
  { label: 'Ransomware share',   metric: 'ransomware_share',  preferredSources: ['verizon_dbir', 'allianz'] },
]

function fmtBenchValue(v: number, unit: string): string {
  if (unit === 'USD') {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
    if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`
    return `$${v.toFixed(0)}`
  }
  if (unit === 'PCT')  return `${v.toFixed(1)}%`
  if (unit === 'DAYS') return `${v.toFixed(0)}d`
  return String(v)
}

function pickLatest(benches: Benchmark[], metric: string, preferred: string[]): Benchmark | null {
  // First, try the preferred sources in order (most-recent year wins).
  for (const src of preferred) {
    const matches = benches.filter(b => b.metric === metric && b.source === src)
    if (matches.length) {
      matches.sort((a, b) => b.year - a.year)
      return matches[0]
    }
  }
  // Fallback: any source for that metric.
  const any = benches.filter(b => b.metric === metric)
  if (!any.length) return null
  any.sort((a, b) => b.year - a.year)
  return any[0]
}

function LossDistributionPanel({ benches }: { benches: Benchmark[] }) {
  const tiles = LOSS_TILES.map(t => ({ tile: t, row: pickLatest(benches, t.metric, t.preferredSources) }))
  const anyData = tiles.some(t => !!t.row)

  // Year-over-year series per metric — sum across sources per year. Used
  // by the inline MiniBarChart to give a quick sense of trend direction
  // without consuming extra panel real-estate.
  const yoySeries = (metric: string): { values: number[]; labels: string[] } => {
    const matching = benches.filter(b => b.metric === metric)
    if (!matching.length) return { values: [], labels: [] }
    // Average within a year if multiple sources reported the same metric.
    const byYear: Record<number, number[]> = {}
    for (const b of matching) {
      if (!byYear[b.year]) byYear[b.year] = []
      byYear[b.year].push(b.value)
    }
    const years = Object.keys(byYear).map(Number).sort()
    return {
      values: years.map(y => {
        const arr = byYear[y]
        return arr.reduce((a, b) => a + b, 0) / arr.length
      }),
      labels: years.map(String),
    }
  }

  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <BarChart3 size={13} className="text-violet" />
        <h3 className="text-[12px] font-semibold text-text-primary">Loss Distribution</h3>
        <span className="text-[10px] text-text-muted">most-recent third-party benchmarks</span>
        <Link to="/app/admin/underwriting-pricing"
              className="ml-auto text-[10px] text-text-muted hover:text-cyan">manage →</Link>
      </div>

      {!anyData ? (
        <div className="text-[11px] text-text-muted py-2">
          No data — admins can populate this at <span className="text-cyan">/app/admin/underwriting-pricing</span>.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {tiles.map(({ tile, row }) => {
            const series = yoySeries(tile.metric)
            return (
              <div key={tile.metric} className="rounded border border-border p-2 bg-surface-card">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[10px] text-text-muted uppercase tracking-wider">{tile.label}</div>
                  {/* Year-over-year micro bar chart — skipped when only one
                      data point is available. */}
                  {series.values.length >= 2 && (
                    <MiniBarChart
                      values={series.values} labels={series.labels}
                      width={80} height={18} tone="violet"
                    />
                  )}
                </div>
                {row ? (
                  <>
                    <div className="text-base font-mono font-semibold text-text-primary mt-0.5">
                      {fmtBenchValue(row.value, row.unit)}
                    </div>
                    <div className="text-[10px] text-text-muted capitalize">
                      {row.source.replace('_', ' ')} {row.year}
                      {row.sector && row.sector !== 'all' && <> · {row.sector}</>}
                    </div>
                  </>
                ) : (
                  <div className="text-[11px] text-text-muted mt-0.5">— not set —</div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}


// ── Regulatory premium-data panel ───────────────────────────────────────────
//
// One card with three sub-tabs: NAIC (US per-carrier or market aggregate),
// EIOPA (EU quarterly by country + LoB), SEC (public-US-carrier 10-K/10-Q
// LLM-extracted cyber-line disclosures). Lots of empty cells the first day
// — connectors are rate-limited and the upstreams can take a while to
// respond.

function fmtUsd(v: number | null): string {
  if (v == null) return '—'
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`
  if (v >= 1_000_000)     return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000)         return `$${(v / 1_000).toFixed(1)}K`
  return `$${v.toFixed(0)}`
}

function fmtEur(v: number | null): string {
  if (v == null) return '—'
  if (v >= 1_000_000_000) return `€${(v / 1_000_000_000).toFixed(2)}B`
  if (v >= 1_000_000)     return `€${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000)         return `€${(v / 1_000).toFixed(1)}K`
  return `€${v.toFixed(0)}`
}

function fmtRatio(v: number | null): string {
  if (v == null) return '—'
  // Stored as 0.0–1.0 by the connectors; surface as percent.
  return `${(v * 100).toFixed(1)}%`
}

type RegTab = 'naic' | 'eiopa' | 'sec'

function RegulatoryPanel(
  { naic, eiopa, sec, spark }:
    { naic: NaicRow[]; eiopa: EiopaRow[]; sec: SecRow[]; spark: RegulatorySpark | null },
) {
  const [tab, setTab] = useState<RegTab>('naic')

  const naicTopYear = useMemo(() => {
    if (!naic.length) return null
    return naic.reduce((max, r) => (r.year > max ? r.year : max), 0)
  }, [naic])

  const naicSparkPts  = (spark?.naic.points  || []).map((v, i) => ({ t: spark?.naic.labels?.[i]  || '', v }))
  const eiopaSparkPts = (spark?.eiopa.points || []).map((v, i) => ({ t: spark?.eiopa.labels?.[i] || '', v }))
  const secSparkPts   = (spark?.sec.points   || []).map((v, i) => ({ t: spark?.sec.labels?.[i]   || '', v }))

  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <Landmark size={13} className="text-emerald" />
        <h3 className="text-[12px] font-semibold text-text-primary">Regulatory premium data</h3>
        <span className="text-[10px] text-text-muted">
          NAIC · EIOPA · SEC EDGAR — refreshed by celery, persisted for trend
        </span>
        <div className="ml-auto inline-flex rounded border border-border overflow-hidden">
          {(['naic', 'eiopa', 'sec'] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={clsx(
                'px-2 py-0.5 text-[11px] uppercase transition-colors',
                tab === t
                  ? 'bg-surface-raised text-text-primary'
                  : 'bg-surface-card text-text-muted hover:bg-surface-raised',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Three side-by-side sparklines — one per regulatory source. Renders
          a 1px dotted baseline + "no data yet" hint when a source's series
          is empty (most likely on first deployment day). */}
      <div className="grid grid-cols-3 gap-2 text-[10px]">
        <div className="rounded border border-border p-1.5 bg-surface-card">
          <div className="text-text-muted uppercase tracking-wider">NAIC GWP · annual</div>
          <SharedSparkline points={naicSparkPts} width={120} height={22}
                            tone="emerald" showDot={true} className="w-full" />
          <div className="text-text-muted truncate">
            {naicSparkPts.length ? `${naicSparkPts.length} year${naicSparkPts.length === 1 ? '' : 's'}` : 'no data yet'}
          </div>
        </div>
        <div className="rounded border border-border p-1.5 bg-surface-card">
          <div className="text-text-muted uppercase tracking-wider">EIOPA GWP · quarterly EU</div>
          <SharedSparkline points={eiopaSparkPts} width={120} height={22}
                            tone="blue" showDot={true} className="w-full" />
          <div className="text-text-muted truncate">
            {eiopaSparkPts.length ? `${eiopaSparkPts.length} quarter${eiopaSparkPts.length === 1 ? '' : 's'}` : 'no data yet'}
          </div>
        </div>
        <div className="rounded border border-border p-1.5 bg-surface-card">
          <div className="text-text-muted uppercase tracking-wider">SEC top carriers · cyber GWP</div>
          <SharedSparkline points={secSparkPts} width={120} height={22}
                            tone="amber" showDot={true} className="w-full" />
          <div className="text-text-muted truncate">
            {secSparkPts.length ? `${secSparkPts.length} period${secSparkPts.length === 1 ? '' : 's'}` : 'no data yet'}
          </div>
        </div>
      </div>

      {tab === 'naic' && (
        naic.length === 0 ? (
          <div className="text-[11px] text-text-muted py-2">
            No NAIC supplement rows yet. Connector polls monthly — check back
            after the next celery cycle, or trigger a manual pull.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-text-muted border-b border-border">
                  <th className="px-2 py-1 w-16">Year</th>
                  <th className="px-2 py-1">Carrier</th>
                  <th className="px-2 py-1 w-24">GWP</th>
                  <th className="px-2 py-1 w-20">Loss ratio</th>
                  <th className="px-2 py-1 w-20">Defense</th>
                  <th className="px-2 py-1 w-20">Policies</th>
                </tr>
              </thead>
              <tbody>
                {naic
                  .filter(r => naicTopYear == null || r.year === naicTopYear)
                  .slice(0, 50)
                  .map(r => (
                    <tr key={r.id} className="border-b border-border hover:bg-surface-raised">
                      <td className="px-2 py-1 font-mono">{r.year}</td>
                      <td className="px-2 py-1">
                        {r.source_url ? (
                          <a href={r.source_url} target="_blank" rel="noreferrer noopener"
                             className="hover:text-cyan inline-flex items-center gap-1">
                            {r.carrier_name}
                            <ExternalLink size={9} className="opacity-50" />
                          </a>
                        ) : r.carrier_name}
                      </td>
                      <td className="px-2 py-1 font-mono">{fmtUsd(r.gross_written_premium)}</td>
                      <td className="px-2 py-1 font-mono">{fmtRatio(r.direct_loss_ratio)}</td>
                      <td className="px-2 py-1 font-mono">{fmtRatio(r.defense_cost_ratio)}</td>
                      <td className="px-2 py-1 font-mono">
                        {r.num_policies != null ? r.num_policies.toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'eiopa' && (
        eiopa.length === 0 ? (
          <div className="text-[11px] text-text-muted py-2">
            No EIOPA Solvency II rows yet. Polled weekly; data lands at quarter end.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-text-muted border-b border-border">
                  <th className="px-2 py-1 w-20">Quarter</th>
                  <th className="px-2 py-1 w-16">Country</th>
                  <th className="px-2 py-1">Line of business</th>
                  <th className="px-2 py-1 w-24">GWP</th>
                  <th className="px-2 py-1 w-24">Claims paid</th>
                </tr>
              </thead>
              <tbody>
                {eiopa.slice(0, 50).map(r => (
                  <tr key={r.id} className="border-b border-border hover:bg-surface-raised">
                    <td className="px-2 py-1 font-mono">{r.quarter}</td>
                    <td className="px-2 py-1 font-mono">{r.country_iso2}</td>
                    <td className="px-2 py-1">{r.line_of_business}</td>
                    <td className="px-2 py-1 font-mono">{fmtEur(r.gross_written_premium_eur)}</td>
                    <td className="px-2 py-1 font-mono">{fmtEur(r.gross_claims_paid_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'sec' && (
        sec.length === 0 ? (
          <div className="text-[11px] text-text-muted py-2">
            No SEC EDGAR cyber-line rows yet. Daily celery pull; quarterly drops.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-text-muted border-b border-border">
                  <th className="px-2 py-1 w-16">Ticker</th>
                  <th className="px-2 py-1">Carrier</th>
                  <th className="px-2 py-1 w-16">Form</th>
                  <th className="px-2 py-1 w-24">Period</th>
                  <th className="px-2 py-1 w-24">Cyber GWP</th>
                  <th className="px-2 py-1 w-20">Loss ratio</th>
                </tr>
              </thead>
              <tbody>
                {sec.slice(0, 30).map(r => (
                  <tr key={r.id} className="border-b border-border hover:bg-surface-raised">
                    <td className="px-2 py-1 font-mono">{r.ticker}</td>
                    <td className="px-2 py-1">
                      {r.source_url ? (
                        <a href={r.source_url} target="_blank" rel="noreferrer noopener"
                           className="hover:text-cyan inline-flex items-center gap-1">
                          {r.carrier_name}
                          <FileText size={9} className="opacity-50" />
                        </a>
                      ) : r.carrier_name}
                    </td>
                    <td className="px-2 py-1 font-mono">{r.form_type}</td>
                    <td className="px-2 py-1 font-mono">{r.period_end_date}</td>
                    <td className="px-2 py-1 font-mono">{fmtUsd(r.cyber_premium_usd)}</td>
                    <td className="px-2 py-1 font-mono">{fmtRatio(r.cyber_loss_ratio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  )
}


// ── Press-release / Business Wire panel ─────────────────────────────────────

function PrItemsPanel(
  { items, spark }: { items: PrItem[]; spark: PrWeeklySpark | null },
) {
  if (!items.length) return null
  // Only show items the LLM actually tagged with a direction OR magnitude —
  // otherwise this devolves into a generic insurance-PR firehose.
  const tagged = items.filter(it => it.direction || it.magnitude_pct != null)
  if (!tagged.length) return null
  const sparkPts = (spark?.points || []).map((v, i) => ({ t: spark?.weeks?.[i] || '', v }))
  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <Newspaper size={13} className="text-amber-light" />
        <h3 className="text-[12px] font-semibold text-text-primary">
          Press releases mentioning cyber premium movement
        </h3>
        <span className="text-[10px] text-text-muted">
          PR Newswire · Business Wire — LLM-tagged
        </span>
        {/* Premium-mention count per ISO-week sparkline. */}
        <SharedSparkline points={sparkPts} width={120} height={20}
                          tone="amber" showDot={true} className="ml-auto" />
      </div>
      <ul className="space-y-1">
        {tagged.slice(0, 12).map(it => (
          <li key={it.id} className="text-[11px] flex items-baseline gap-2">
            <span className="font-mono text-text-muted text-[10px] w-20 truncate">
              {it.feed.replace('_', ' ')}
            </span>
            {it.magnitude_pct != null && (
              <span className={clsx(
                'font-mono w-14',
                it.magnitude_pct >= 0 ? 'text-rose-light' : 'text-emerald-light',
              )}>
                {it.magnitude_pct >= 0 ? '+' : ''}{it.magnitude_pct.toFixed(1)}%
              </span>
            )}
            {it.direction && (
              <span className="uppercase text-[9px] text-text-muted">
                {it.direction}
              </span>
            )}
            <a
              href={it.url || '#'}
              target="_blank"
              rel="noreferrer noopener"
              className="text-text-primary hover:text-cyan flex-1 truncate"
            >
              {it.title}
            </a>
            {it.published_at && (
              <span className="text-text-muted text-[10px]">
                {formatRelative(it.published_at)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}


// ── Inline mini-charts above the advisories table + reinsurance panel ──────


function AdvisoriesDailyMini({ spark }: { spark: AdvisoriesDailySpark | null }) {
  if (!spark || !spark.days || spark.days.length === 0) {
    return <span className="text-text-muted text-[9px] italic">no advisory series yet</span>
  }
  // Stacked-area-ish view: render the *total* per day as a single sparkline
  // plus a tiny stack of per-carrier mini-bars beneath the totals. Keeps the
  // table-header strip narrow enough to coexist with the "last pull" badge.
  const carriers = Object.keys(spark.by_carrier).sort()
  const totals = spark.days.map((_, i) =>
    carriers.reduce((sum, c) => sum + (spark.by_carrier[c]?.[i] || 0), 0),
  )
  const sparkPts = totals.map((v, i) => ({ t: spark.days[i], v }))
  return (
    <span className="inline-flex items-center gap-1 ml-2">
      <SharedSparkline
        points={sparkPts} width={120} height={18} tone="muted"
        showDot={true} showDelta={false}
      />
      <span className="text-text-muted text-[9px]">
        {totals.reduce((a, b) => a + b, 0)} total · {spark.days.length}d
      </span>
    </span>
  )
}


function ReinsurancePulsePanel(
  { spark }: { spark: ReinsuranceWeeklySpark | null },
) {
  if (!spark || !spark.weeks || spark.weeks.length === 0) {
    // Hide the panel entirely on empty data — keeps the page clean before
    // the reinsurance-pulse celery task has run.
    return null
  }
  const carriers = Object.keys(spark.by_carrier).sort()
  if (carriers.length === 0) return null
  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <Activity size={13} className="text-cyan" />
        <h3 className="text-[12px] font-semibold text-text-primary">Reinsurance pulse</h3>
        <span className="text-[10px] text-text-muted">
          advisories per reinsurer per week — last {spark.weeks.length} weeks
        </span>
      </div>
      <div className="space-y-1.5">
        {carriers.map(cid => {
          const values = spark.by_carrier[cid] || []
          const total = values.reduce((a, b) => a + b, 0)
          return (
            <div key={cid} className="flex items-center gap-2 text-[11px]">
              <span className="capitalize w-28 truncate" title={cid}>
                {cid.replace(/_/g, ' ')}
              </span>
              <MiniBarChart
                values={values} labels={spark.weeks}
                width={200} height={20} tone="cyan"
                className="flex-1"
              />
              <span className="font-mono text-[10px] text-text-muted w-12 text-right">
                {total} total
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
